import CancelIcon from '@material-design-icons/svg/filled/cancel.svg?react';
import ErrorIcon from '@material-design-icons/svg/filled/error.svg?react';
import OpenInNewIcon from '@material-design-icons/svg/filled/open_in_new.svg?react';
import ScheduleIcon from '@material-design-icons/svg/filled/schedule.svg?react';
import WarningIcon from '@material-design-icons/svg/filled/warning.svg?react';
import { formatDateTimeWithTimeFirst } from '../../../../lib/hubspot-management/formatters';
import { cn } from '../../../../lib/utils';
import type { HubspotPanelTab } from '../../../../stores/hubspotPanelStore';
import { getRequestTypeDisplay } from '../../../../utils/hubspotUtils';
import { formatFileSize } from '../../../../utils/stringHelpers';
import { CopyableValue, Field, FieldGrid } from '../../../common/detailParts';
import {
  ChecksChip,
  getTicketVersionLabel,
} from '../../../hubspot-management/table/HubspotTicketCells';
import { ApkDownloadMenu } from '../../ApkDownloadMenu';
import { PanelSection } from '../../PanelSection';
import type {
  AndroidDetails,
  HubspotTicketAutomatedReviewData,
  HubspotTicketDetailData,
} from '../../types';
import { getAndroidVersionName } from '../../utils';

type ReviewCheck = HubspotTicketAutomatedReviewData['checks'][number];

function getAttentionPresentation(check: ReviewCheck) {
  if (check.executionStatus === 'Running') {
    return {
      label: 'Running',
      Icon: ScheduleIcon,
      className: 'text-amber-300',
    };
  }

  switch (check.effectiveConclusion) {
    case 'Failed':
      return { label: 'Failed', Icon: CancelIcon, className: 'text-red-300' };
    case 'Error':
      return { label: 'Error', Icon: ErrorIcon, className: 'text-rose-300' };
    case 'Warning':
      return {
        label: 'Warning',
        Icon: WarningIcon,
        className: 'text-amber-300',
      };
    default:
      return null;
  }
}

function Stat({
  label,
  value,
  className,
}: {
  label: string;
  value: number;
  className: string;
}) {
  return (
    <div className="rounded-lg bg-white/[0.03] px-3 py-2">
      <div className={cn('text-lg font-semibold tabular-nums', className)}>
        {value}
      </div>
      <div className="text-[11px] uppercase tracking-wide text-neutral-500">
        {label}
      </div>
    </div>
  );
}

function TabLink({
  onClick,
  children,
}: {
  onClick: () => void;
  children: string;
}) {
  return (
    <div className="text-xs">
      <button
        type="button"
        onClick={onClick}
        className="font-medium text-teal-300 transition-colors hover:text-teal-200"
      >
        {children} →
      </button>
    </div>
  );
}

interface HubspotTicketChecksSummaryProps {
  summary: HubspotTicketDetailData['latestAutomatedReviewSummary'];
  review: HubspotTicketAutomatedReviewData | null;
  onOpenTab: (tab: HubspotPanelTab) => void;
}

/** The decision-relevant slice of the automated review. */
export function HubspotTicketChecksSummary({
  summary,
  review,
  onOpenTab,
}: HubspotTicketChecksSummaryProps) {
  const attentionChecks =
    review?.checks
      .map((check) => ({
        check,
        presentation: getAttentionPresentation(check),
      }))
      .filter(
        (
          entry,
        ): entry is {
          check: ReviewCheck;
          presentation: NonNullable<
            ReturnType<typeof getAttentionPresentation>
          >;
        } => entry.presentation !== null,
      ) ?? [];

  return (
    <PanelSection
      title="Automated checks"
      trailing={<ChecksChip summary={summary} />}
    >
      {summary ? (
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <Stat
              label="Passed"
              value={summary.effectivePassedCount}
              className="text-emerald-300"
            />
            <Stat
              label="Failed"
              value={summary.effectiveFailedCount}
              className={
                summary.effectiveFailedCount > 0
                  ? 'text-red-300'
                  : 'text-neutral-300'
              }
            />
            <Stat
              label="Warnings"
              value={summary.effectiveWarningCount}
              className={
                summary.effectiveWarningCount > 0
                  ? 'text-amber-300'
                  : 'text-neutral-300'
              }
            />
            <Stat
              label="Skipped"
              value={summary.effectiveSkippedCount}
              className="text-neutral-300"
            />
          </div>

          {attentionChecks.length > 0 && (
            <ul className="divide-y divide-white/5 overflow-hidden rounded-lg border border-white/5">
              {attentionChecks.slice(0, 5).map(({ check, presentation }) => (
                <li
                  key={check.id}
                  className="flex items-start gap-2.5 px-3 py-2"
                >
                  <presentation.Icon
                    className={cn(
                      'mt-0.5 size-4 shrink-0 fill-current',
                      presentation.className,
                    )}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="text-sm text-white">
                      {check.displayName}
                    </div>
                    {check.summary && (
                      <div className="truncate text-xs text-neutral-400">
                        {check.summary}
                      </div>
                    )}
                  </div>
                  <span
                    className={cn(
                      'shrink-0 text-xs font-medium',
                      presentation.className,
                    )}
                  >
                    {presentation.label}
                  </span>
                </li>
              ))}
              {attentionChecks.length > 5 && (
                <li className="px-3 py-2 text-xs text-neutral-500">
                  +{attentionChecks.length - 5} more
                </li>
              )}
            </ul>
          )}

          <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-neutral-500">
            <span>
              {summary.lastCompletedAt
                ? `Last run ${formatDateTimeWithTimeFirst(summary.lastCompletedAt)}`
                : 'Run in progress'}
              {summary.hasOverrides && ' · includes a manual override'}
            </span>
            <TabLink onClick={() => onOpenTab('checks')}>All checks</TabLink>
          </div>
        </div>
      ) : (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <span className="text-sm text-neutral-400">
            No automated review has run for this submission yet.
          </span>
          <TabLink onClick={() => onOpenTab('checks')}>Run a check</TabLink>
        </div>
      )}
    </PanelSection>
  );
}

interface HubspotTicketSubmissionFactsProps {
  data: HubspotTicketDetailData;
  androidDetails?: AndroidDetails;
  categorizedPermissions: {
    normal: string[];
    dangerous: string[];
    special: string[];
  };
  apkFileUrl: string | null;
  links: {
    website: string | null;
    privacyPolicy: string | null;
    license: string | null;
  };
  onOpenTab: (tab: HubspotPanelTab) => void;
}

/** What the APK and ticket say about themselves, in one scan. */
export function HubspotTicketSubmissionFacts({
  data,
  androidDetails,
  categorizedPermissions,
  apkFileUrl,
  links,
  onOpenTab,
}: HubspotTicketSubmissionFactsProps) {
  const packageName = androidDetails?.android_package ?? data.dappPackageName;
  const version = getTicketVersionLabel(data) ?? data.releaseVersion;
  const permissionCount = androidDetails?.permissions?.length ?? 0;
  const linkEntries = [
    { label: 'Website', href: links.website },
    { label: 'Privacy policy', href: links.privacyPolicy },
    { label: 'Terms', href: links.license },
  ].filter((entry): entry is { label: string; href: string } =>
    Boolean(entry.href),
  );

  return (
    <PanelSection title="Submission">
      <FieldGrid cols={2}>
        <Field label="Package">
          {packageName ? (
            <CopyableValue value={packageName} className="-my-1.5" />
          ) : (
            '—'
          )}
        </Field>
        <Field label="Version">{version ?? '—'}</Field>
        <Field label="APK">
          <div className="flex flex-wrap items-center gap-2">
            <span>
              {data.apkFileSize != null
                ? formatFileSize(data.apkFileSize)
                : '—'}
            </span>
            {apkFileUrl && <ApkDownloadMenu url={apkFileUrl} fileSize={null} />}
          </div>
        </Field>
        <Field label="Min SDK">
          {androidDetails
            ? `${androidDetails.min_sdk} (${getAndroidVersionName(androidDetails.min_sdk)})`
            : '—'}
        </Field>
        <Field label="Permissions">
          {permissionCount > 0 ? (
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <span>{permissionCount} total</span>
              {categorizedPermissions.dangerous.length > 0 && (
                <span className="text-yellow-300">
                  {categorizedPermissions.dangerous.length} dangerous
                </span>
              )}
              {categorizedPermissions.special.length > 0 && (
                <span className="text-red-300">
                  {categorizedPermissions.special.length} special
                </span>
              )}
              <TabLink onClick={() => onOpenTab('details')}>View</TabLink>
            </div>
          ) : (
            '—'
          )}
        </Field>
        <Field label="Locales">
          {androidDetails?.locales?.length
            ? androidDetails.locales.join(', ')
            : '—'}
        </Field>
        <Field label="Request">
          {getRequestTypeDisplay(data.requestType).text}
        </Field>
        <Field label="Source">{data.submissionSource}</Field>
        <Field label="Submissions">{data.submissionCount}</Field>
        {data.hsTicketPriority && (
          <Field label="Priority">{data.hsTicketPriority}</Field>
        )}
        <Field label="Created">
          {formatDateTimeWithTimeFirst(data.createdAt)}
        </Field>
        <Field label="Modified">
          {formatDateTimeWithTimeFirst(data.lastModifiedAt)}
        </Field>
        {data.closedDate && (
          <Field label="Closed">
            <span className="text-green-400">
              {formatDateTimeWithTimeFirst(data.closedDate)}
            </span>
          </Field>
        )}
      </FieldGrid>

      {linkEntries.length > 0 && (
        <div className="mt-5 flex flex-wrap gap-2 border-t border-white/5 pt-4">
          {linkEntries.map((entry) => (
            <a
              key={entry.label}
              href={entry.href}
              target="_blank"
              rel="noopener noreferrer"
              title={entry.href}
              className="inline-flex items-center gap-1.5 rounded-md border border-neutral-800 bg-neutral-900/60 px-2.5 py-1.5 text-xs text-neutral-200 transition-colors hover:border-neutral-700 hover:text-white"
            >
              {entry.label}
              <OpenInNewIcon className="size-3.5 fill-current text-neutral-500" />
            </a>
          ))}
        </div>
      )}
    </PanelSection>
  );
}
